/// <amd-module name="TFS/Views/SuiteMePage/SuiteMePageView/SuiteMePageView" />
import React = require("react");

import * as SuiteMePageResources from "TFS/Views/SuiteMePage/Resources";
import { IObservableValue, IReadonlyObservableArray, ObservableValue } from "VSS/Core/Observable";
import { getTabs } from "VSS/Features/PlatformUI/ContributedTab";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { Components, IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { IVssPageService } from "VSS/Platform/Page";
import { Header, TitleSize } from "VSSUI/Header";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { IVssContributedTab, TabBar, TabContent } from "VSSUI/Tabs";
import { ZeroData } from "VSSUI/ZeroData";
import { IProjectsPivotView, IProjectsPivotViewSerivce } from "./ProjectsPivotService";
import { SuiteMePageViewComponentProps } from "./SuiteMePageView.Props";

interface SuiteMePageViewComponentState {
    hasProjects?: IObservableValue<boolean | undefined>;
    hostName: string;
    projectsView?: IProjectsPivotView;
    selectedTab: ObservableValue<string>;
    tabs: IReadonlyObservableArray<IVssContributedTab>;
}

class SuiteMePageViewComponent extends VssComponent<SuiteMePageViewComponentProps, SuiteMePageViewComponentState> {
    public constructor(props: SuiteMePageViewComponentProps, context: IVssComponentContext) {
        super(props, context);

        let projectsView: IProjectsPivotView | undefined;
        let hasProjects: IObservableValue<boolean | undefined> | undefined;

        // Intialize the selectedTab based on the page we are currently rendering.
        const navigationService = context.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        const routeValues = navigationService.getCurrentRoute().routeValues;
        const selectedTab = new ObservableValue(routeValues["view"] || routeValues["contributionId"] || "projects");

        if (selectedTab.value === "projects") {
            projectsView = context.pageContext.getService<IProjectsPivotViewSerivce>("IProjectsPivotViewSerivce").getProjectsPivotView();

            // @NOTE: Having projects or not is async, we should try to make it synchronous if possible.
            //  It is causes a two pass render on the hub.
            hasProjects = projectsView.hasProjects;
        }

        this.state = {
            hasProjects,
            hostName: context.pageContext.getService<IVssPageService>("IVssPageService").getData()!.hostName,
            projectsView,
            selectedTab,
            tabs: getTabs(context.pageContext, "ms.vss-tfs-web.suite-home-pivots", {
                defaultRouteId: "ms.vss-tfs-web.suite-me-page-ext-route",
                fpsRequired: true,
                updateUrlOnPivotChange: true
            })
        };
    }

    public render(): JSX.Element {
        const { hasProjects, hostName, projectsView, selectedTab, tabs } = this.state;

        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        if (contributionService.getDataProviderExceptions()["ms.vss-tfs-web.project-mru-data-provider"] ||
            contributionService.getDataProviderExceptions()["ms.vss-tfs-web.project-plus-product-data-provider"]) {
            // Exception occurred while retrieving build data so show error page instead of assuming there are no projects
            return (
                <div className="suite-me-page-view-zero-data">
                    <ZeroData
                        primaryText={SuiteMePageResources.ErrorMessage}
                        imagePath={getAssetLocation(this.context.pageContext, "ms.vss-tfs-web/suite-me-page-content/images/no-results.png")}
                        imageAltText=""
                    />
                </div>
            )
        }

        return (
            <div className="suite-me-page-view flex flex-grow flex-stretch v-scroll-auto absolute-fill">
                <div className="suite-me-page-view-container flex flex-column flex-grow v-scroll-auto">
                    <Surface background={SurfaceBackground.neutral}>
                        <Page className="flex-grow custom-scrollbar scroll-auto-hide" selectedTabId={selectedTab} tabProviders={tabs}>
                            <Observer hasProjects={hasProjects}>
                                {(props: { hasProjects?: boolean }) => {
                                    return props.hasProjects !== false ? (
                                        <React.Fragment>
                                            <Header title={hostName} titleSize={TitleSize.Large} className="hostname-header flex-noshrink" />
                                            <TabBar className="suite-me-page-tabbar flex-wrap" onSelectedTabChanged={this.onSelectedTabChanged} />
                                            <TabContent />
                                        </React.Fragment>
                                    ) : projectsView ? (
                                        <div className="flex flex-grow justify-center">
                                            <img
                                                className="inline-create-project-image"
                                                src={getAssetLocation(
                                                    this.context.pageContext,
                                                    "ms.vss-tfs-web/suite-me-page-content/images/zero-data-first-project.png"
                                                )}
                                                alt={SuiteMePageResources.ZeroProjectsHeader}
                                            />
                                            <div className="flex-column inline-create-project-form">
                                                <div className="inline-create-project-header title-l">{SuiteMePageResources.ZeroProjectsHeader}</div>
                                                <WrappedComponent
                                                    dependencies={["ms.vss-tfs-web.create-project-panel"]}
                                                    wrappedType={"createProjectComponent"}
                                                    className="flex-grow"
                                                    invokingSource={projectsView.projectCreateUrlParameters.source || "SuiteMePage"}
                                                    isLightWeightView={projectsView.useSimpleProjectCreate}
                                                    preCreatedProjectId={projectsView.preCreatedProjectId}
                                                    processTemplate={projectsView.projectCreateUrlParameters.processTemplate}
                                                    versionControlType={projectsView.projectCreateUrlParameters.versionControl}
                                                    acquisitionId={projectsView.projectCreateUrlParameters.acquisitionId}
                                                    product={projectsView.projectCreateUrlParameters.product}
                                                    defaultFeatureIds={
                                                        projectsView.projectCreateUrlParameters.featureId
                                                            ? [projectsView.projectCreateUrlParameters.featureId]
                                                            : undefined
                                                    }
                                                    projectVisibility={projectsView.projectCreateUrlParameters.projectVisibility}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div />
                                    );
                                }}
                            </Observer>
                        </Page>
                    </Surface>
                </div>
            </div>
        );
    }

    private onSelectedTabChanged = (newTabId: string) => {
        this.state.selectedTab.value = newTabId;
    };
}

Components.add("suiteMePageView", SuiteMePageViewComponent);
